import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { LoadingStatus } from 'GeminiViewerComponent/helpers/AsyncStatus';
import { mediaService } from '../services/media.service';

const initialState = {
    mediaStatus: LoadingStatus.Idle,
    media: [],
    mediaPageInfo: {},
    mediaLoadedRowsMap: {},
    mediaById: {},
    mediaByNodeId: {},
    contentMediaByNodeId: {},
};

export const fetchAllMedia = createAsyncThunk(
    'media/fetchAllMedia',
    async (params, { getState }) =>
        await mediaService.getAllByRange(
            params.startIndex ?? 0,
            params.stopIndex ?? 0,
            params.searchString,
            params.sort,
            getState().accounts.activeUser.s3_prefix,
            params.mediaType,
            params.minFileSize,
            params.maxFileSize,
            params.minMediaWidth,
            params.maxMediaWidth,
            params.minMediaHeight,
            params.maxMediaHeight,
            params.draft,
            params.lastModifiedDate
        )
);

export const createMedia = createAsyncThunk(
    'media/createMedia',
    async (newMedia, { getState }) => await mediaService.create(newMedia, getState().accounts.activeUser.s3_prefix)
);

export const updateMedia = createAsyncThunk(
    'media/updateMedia',
    async (params, { getState }) => await mediaService.update(params, getState().accounts.activeUser.s3_prefix)
);

export const deleteMedia = createAsyncThunk('media/deleteMedia', async (mediaId, { rejectWithValue }) => {
    try {
        await mediaService.delete(mediaId);
        return { id: mediaId };
    } catch (err) {
        return rejectWithValue(err?.response?.data ?? err.message);
    }
});

export const fetchContentMediaByNode = createAsyncThunk(
    'media/fetchContentMediaByNode',
    async ({ procedure_id, node_id, procedure_session_id }, { getState }) => {
        const prefix = getState().accounts.activeUser.s3_prefix;
        const media = await mediaService.fetchMediaByNodeId(procedure_id, node_id, procedure_session_id, prefix);
        return { node_id, media };
    }
);

export const fetchContentMediaForNodes = createAsyncThunk(
    'media/fetchContentMediaForNodes',
    async ({ procedure_id, nodes, procedure_session_id }, { dispatch }) => {
        await Promise.all(nodes.map((node) => dispatch(fetchContentMediaByNode({ procedure_id, node_id: node.id, procedure_session_id }))));
        return true;
    }
);

export const fetchMediaById = createAsyncThunk('media/fetchMediaById', async (mediaId, { getState }) => {
    const prefix = getState().accounts.activeUser.s3_prefix;
    const media = await mediaService.fetchMediaById(mediaId, prefix);
    return media;
});

export const fetchMediaByNode = createAsyncThunk(
    'media/fetchMediaByNode',
    async ({ procedure_id, node_id, procedureSessionId }, { getState }) => {
        const prefix = getState().accounts.activeUser.s3_prefix;
        const medias = await mediaService.fetchMediaByNodeId(procedure_id, node_id, procedureSessionId, prefix);

        return { node_id, medias };
    }
);

const mediaSlice = createSlice({
    name: 'media',
    initialState,
    reducers: {
        resetMediaState: () => {
            return initialState;
        },
        resetMediaStatus: (state) => {
            state.mediaStatus = LoadingStatus.Idle;
            state.media = [];
            state.mediaPageInfo = {};
            state.mediaLoadedRowsMap = {};

            state.mediaById = {};
            state.mediaByNodeId = {};
        },
    },
    extraReducers: (builder) => {
        builder
            // create
            .addCase(createMedia.pending, (state) => {
                state.mediaStatus = LoadingStatus.Loading;
            })
            .addCase(createMedia.fulfilled, (state, action) => {
                state.mediaStatus = LoadingStatus.Idle;

                const media = action.payload;
                const id = media?.media_id ?? media?.id;
                if (id != null) state.mediaById[id] = media;
            })
            .addCase(createMedia.rejected, (state) => {
                state.mediaStatus = LoadingStatus.Failed;
            })

            // update
            .addCase(updateMedia.pending, (state) => {
                state.mediaStatus = LoadingStatus.Loading;
            })
            .addCase(updateMedia.fulfilled, (state, action) => {
                state.mediaStatus = LoadingStatus.Idle;

                const media = action.payload;
                const id = media?.media_id ?? media?.id;
                if (id != null) state.mediaById[id] = media;
            })
            .addCase(updateMedia.rejected, (state) => {
                state.mediaStatus = LoadingStatus.Failed;
            })

            // delete
            .addCase(deleteMedia.fulfilled, (state, action) => {
                state.mediaStatus = LoadingStatus.Idle;

                // 1) normalized ID (main: media_id)
                const deletedId = action.payload?.media_id;
                if (deletedId == null) return;

                const deletedIdStr = String(deletedId);

                // 2) nodeId (main: node_id)
                const payloadNodeId = action.payload?.node_id;

                const mediaFromMap = state.mediaById?.[deletedId];
                const mapNodeId = mediaFromMap?.node_id;

                let nodeIdToUse = payloadNodeId ?? mapNodeId;

                // 3) If there is no node_id, search for it by browsing contentMediaByNodeId
                if (nodeIdToUse == null) {
                    for (const [nodeKey, arr] of Object.entries(state.contentMediaByNodeId)) {
                        const idx = (arr ?? []).findIndex((m) => String(m?.media_id ?? m?.id) === deletedIdStr);
                        if (idx >= 0) {
                            nodeIdToUse = nodeKey;
                            break;
                        }
                    }
                }

                // 4) Delete from the map by nodeId
                if (nodeIdToUse != null) {
                    const nodeKey = nodeIdToUse;
                    const mediaNode = state.contentMediaByNodeId?.[nodeKey];

                    if (Array.isArray(mediaNode) && mediaNode.length > 0) {
                        const idx = mediaNode.findIndex((m) => String(m?.media_id ?? m?.id) === deletedIdStr);
                        if (idx >= 0) {
                            mediaNode.splice(idx, 1);
                            state.contentMediaByNodeId[nodeKey] = mediaNode;
                        }
                    }
                }

                // 5) Delete from the main map
                if (state.mediaById) {
                    delete state.mediaById[deletedId];
                }

                state.mediaStatus = LoadingStatus.Idle;
            })

            .addCase(deleteMedia.pending, (state) => {
                state.mediaStatus = LoadingStatus.Loading;
            })
            .addCase(deleteMedia.rejected, (state) => {
                state.mediaStatus = LoadingStatus.Failed;
            })

            // fetch by id
            .addCase(fetchMediaById.pending, (state) => {
                state.mediaStatus = LoadingStatus.Loading;
            })
            .addCase(fetchMediaById.fulfilled, (state, action) => {
                state.mediaStatus = LoadingStatus.Loaded;

                const media = action.payload;
                const id = media?.media_id ?? media?.id;
                if (id != null) state.mediaById[id] = media;
            })
            .addCase(fetchMediaById.rejected, (state) => {
                state.mediaStatus = LoadingStatus.Failed;
            })

            .addCase(fetchMediaByNode.pending, (state) => {
                state.mediaStatus = LoadingStatus.Loading;
            })
            .addCase(fetchMediaByNode.fulfilled, (state, action) => {
                state.mediaStatus = LoadingStatus.Loaded;

                const { node_id, medias } = action.payload;
                state.mediaByNodeId[node_id] = medias;
                state.contentMediaByNodeId[node_id] = medias;
                for (const m of medias ?? []) {
                    const id = m?.media_id ?? m?.id;
                    if (id != null) state.mediaById[id] = m;
                }
            })
            .addCase(fetchMediaByNode.rejected, (state) => {
                state.mediaStatus = LoadingStatus.Failed;
            })

            .addCase(fetchAllMedia.pending, (state) => {
                state.mediaStatus = LoadingStatus.Loading;
            })
            .addCase(fetchAllMedia.fulfilled, (state, action) => {
                state.mediaStatus = LoadingStatus.Loaded;
                state.mediaPageInfo = action.payload.pageInfo;

                if (action.meta.arg.reset) {
                    state.mediaLoadedRowsMap = {};
                }

                const startIndex = action.meta.arg.startIndex;
                const stopIndex = startIndex + action.payload.medias.length - 1;

                for (let i = startIndex; i <= stopIndex; i++) {
                    state.mediaLoadedRowsMap[i] = LoadingStatus.Loaded;
                }

                for (let j = stopIndex + 1; j <= action.payload?.pageInfo?.TotalCount; j++) {
                    delete state.mediaLoadedRowsMap[j];
                }

                if (action.meta.arg.reset) {
                    state.media = action.payload.medias;
                } else {
                    state.media = state.media.concat(action.payload.medias);
                }

                for (const m of action.payload.medias ?? []) {
                    const id = m?.media_id ?? m?.id;
                    if (id != null) state.mediaById[id] = m;
                }
            })
            .addCase(fetchAllMedia.rejected, (state) => {
                state.mediaStatus = LoadingStatus.Failed;
            })
            .addCase(fetchContentMediaByNode.pending, (state) => {
                state.mediaStatus = LoadingStatus.Loading;
            })
            .addCase(fetchContentMediaByNode.fulfilled, (state, action) => {
                const { node_id, media } = action.payload;
                state.contentMediaByNodeId[node_id] = media ?? [];
            })

            .addCase(fetchContentMediaByNode.rejected, (state) => {
                state.mediaStatus = LoadingStatus.Failed;
            });
    },
});

export const getMediaStatus = (state) => state.media.mediaStatus;
export const selectMedia = (state) => state.media.media;
export const getMediaPageInfo = (state) => state.media.mediaPageInfo;

export const getMediaById = (state, mediaId) =>
    state.media.mediaById?.[mediaId] ?? state.media.media.find((a) => a.media_id === mediaId);

export const { resetMediaState, resetMediaStatus } = mediaSlice.actions;

export const getContentMediaByNodeId = (state) => state.media.contentMediaByNodeId;

export const getMediaContent = (state) =>
    state.activeTree?.nodes?.map((node) => ({
        nodeId: node.id,
        media: state.media.contentMediaByNodeId?.[node.id] ?? [],
    }));

export default mediaSlice.reducer;
